import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Search, HelpCircle, History } from "lucide-react";
import CookieConsent from "../components/CookieConsent";
import ScrollToTop from "../components/ScrollToTop";
import LanguageSwitcher from "../components/LanguageSwitcher";
import RequestCodeForm from "../components/RequestCodeForm";
import { useLanguage } from "../contexts/LanguageContext";
import { useSearchHistory } from "../hooks/useSearchHistory";
import axios from "axios";
import { useAnalytics } from "../hooks/useAnalytics";

const BACKEND_URL = window.location.origin;
const APP_DOMAIN = process.env.REACT_APP_DOMAIN || window.location.origin;
const DOWNLOADER_URL = process.env.REACT_APP_DOWNLOADER_URL || 'https://aftv.news';
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { addToHistory, searchHistory } = useSearchHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCookie, setShowCookie] = useState(true);
  const [totalCodes, setTotalCodes] = useState(135496);
  const [topSearches, setTopSearches] = useState([]);
  const [footerText, setFooterText] = useState("© 2025 FireDL. All rights reserved. Your source for the best working Downloader codes.");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const [adsenseSettings, setAdsenseSettings] = useState({ enabled: false, code: "" });
  const [logoText, setLogoText] = useState("FIREDL");
  const [searchInstructionText, setSearchInstructionText] = useState("Type the apk name & press enter for a list of working Downloader codes");
  
  // Track page view
  useAnalytics("Homepage");

  // Debounce suggestions
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 2) {
        fetchSuggestions(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    // Fetch total codes count
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setTotalCodes(response.data.totalCodes);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    
    // Fetch top searches
    const fetchTopSearches = async () => {
      try {
        const response = await axios.get(`${API}/top-searches`);
        setTopSearches(response.data.top_searches);
      } catch (error) {
        console.error("Error fetching top searches:", error);
      }
    };
    
    // Fetch footer settings
    const fetchFooterSettings = async () => {
      try {
        const response = await axios.get(`${API}/footer-settings`);
        setFooterText(response.data.text);
      } catch (error) {
        console.error("Error fetching footer settings:", error);
        // Keep default footer text if API fails
      }
    };

    // Fetch AdSense settings
    const fetchAdSenseSettings = async () => {
      try {
        const response = await axios.get(`${API}/adsense-settings`);
        setAdsenseSettings(response.data);
      } catch (error) {
        console.error("Error fetching AdSense settings:", error);
      }
    };

    // Fetch logo settings
    const fetchLogoSettings = async () => {
      try {
        const response = await axios.get(`${API}/logo-settings`);
        setLogoText(response.data.text);
      } catch (error) {
        console.error("Error fetching logo settings:", error);
        // Keep default logo text if API fails
      }
    };

    // Fetch search instruction settings
    const fetchSearchInstructionSettings = async () => {
      try {
        const response = await axios.get(`${API}/search-instruction-settings`);
        setSearchInstructionText(response.data.text);
      } catch (error) {
        console.error("Error fetching search instruction settings:", error);
        // Keep default text if API fails
      }
    };
    
    fetchStats();
    fetchTopSearches();
    fetchFooterSettings();
    fetchAdSenseSettings();
    fetchLogoSettings();
    fetchSearchInstructionSettings();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Add to search history
      addToHistory(searchQuery.trim());
      navigate(`/searchresults?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  const handleHistorySearch = (query) => {
    setSearchQuery(query);
    addToHistory(query);
    navigate(`/searchresults?q=${encodeURIComponent(query)}`);
  };

  const handleFAQ = () => {
    navigate("/faq");
  };

  const fetchSuggestions = async (query) => {
    // Always include history suggestions when showing suggestions
    const historySuggestions = searchHistory.map(item => ({
      type: 'history',
      name: item.query,
      query: item.query,
      timestamp: item.timestamp
    }));

    if (query.length < 2) {
      // Show only history when query is short
      setSuggestions(historySuggestions);
      setShowSuggestions(historySuggestions.length > 0);
      return;
    }

    try {
      const response = await axios.get(`${API}/search-suggestions?q=${encodeURIComponent(query)}`);
      const apiSuggestions = (response.data.suggestions || []).map(suggestion => ({
        type: 'api',
        name: suggestion,
        query: suggestion
      }));
      
      // Filter history to show only relevant matches
      const filteredHistory = historySuggestions.filter(item =>
        item.query.toLowerCase().includes(query.toLowerCase())
      );
      
      // Combine suggestions: history first, then API suggestions
      const combinedSuggestions = [
        ...filteredHistory.slice(0, 3), // Max 3 history items
        ...apiSuggestions.slice(0, 7)   // Max 7 API suggestions
      ];
      
      setSuggestions(combinedSuggestions);
      setShowSuggestions(combinedSuggestions.length > 0);
      setSelectedSuggestion(-1);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      // Fallback to history only
      setSuggestions(historySuggestions);
      setShowSuggestions(historySuggestions.length > 0);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Always fetch suggestions (including history) when user types
    fetchSuggestions(value);
  };

  // Show suggestions when input is focused (including history)
  const handleInputFocus = () => {
    fetchSuggestions(searchQuery);
  };

  const handleSuggestionClick = (suggestion) => {
    const query = suggestion.query || suggestion.name || suggestion;
    setSearchQuery(query);
    setShowSuggestions(false);
    addToHistory(query);
    navigate(`/searchresults?q=${encodeURIComponent(query)}`);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestion(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestion >= 0 && suggestions[selectedSuggestion]) {
        handleSuggestionClick(suggestions[selectedSuggestion]);
      } else {
        handleSearch(e);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestion(-1);
    }
  };

  return (
    <>
      <Helmet>
        <title>FireDL - Best Working Downloader Codes for Firestick & Android TV 2025</title>
        <meta name="description" content={`Find ${totalCodes.toLocaleString()}+ working Downloader codes for Firestick and Android TV. Updated daily with verified codes for streaming apps, secret app stores, and more. Copy codes instantly!`} />
        <meta name="keywords" content="Downloader codes Firestick, Android TV downloader codes, working downloader codes 2025, Firestick streaming apps, secret app stores, verified codes, FireDL codes" />
        <link rel="canonical" href={`${APP_DOMAIN}/`} />
        
        {/* Open Graph */}
        <meta property="og:title" content="FireDL - Best Working Downloader Codes for Firestick & Android TV" />
        <meta property="og:description" content={`${totalCodes.toLocaleString()}+ verified Downloader codes updated daily. Copy working codes for streaming apps instantly.`} />
        <meta property="og:url" content={`${APP_DOMAIN}/`} />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:title" content="FireDL - Best Working Downloader Codes for Firestick & Android TV" />
        <meta name="twitter:description" content={`${totalCodes.toLocaleString()}+ verified Downloader codes updated daily.`} />
        
        {/* Structured Data for Homepage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "FireDL",
            "url": APP_DOMAIN,
            "description": `Find ${totalCodes.toLocaleString()}+ working Downloader codes for Firestick and Android TV. Updated daily with verified codes for streaming apps and secret app stores.`,
            "applicationCategory": "EntertainmentApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${APP_DOMAIN}/searchresults?search={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      {/* Language Switcher - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      
      {/* Skip to content link */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded">
        Go to content
      </a>

      {/* Main Content */}
      <main id="main-content" className="w-full max-w-4xl mx-auto text-center space-y-8 sm:space-y-12">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="logo-container">
            <h1 className="logo-text text-5xl sm:text-6xl lg:text-7xl">{logoText}</h1>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-lg sm:text-xl text-gray-800 font-medium px-4">
          {t('tagline')}
        </p>

        {/* Counter */}
        <p className="text-base sm:text-lg text-blue-500 px-4">
          {totalCodes.toLocaleString()} Active Downloader Codes So far...
        </p>

        {/* Search Section */}
        <div className="space-y-4 sm:space-y-6 px-4">
          <p className="text-sm sm:text-base text-gray-700">
            {searchInstructionText}
          </p>
          
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={() => {
                  // Delay hiding suggestions to allow clicking
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 pr-12 text-sm sm:text-base border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={t('searchPlaceholder')}
                autoComplete="off"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-blue-600 transition-colors"
                aria-label={t('search')}
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors ${
                        selectedSuggestion === index ? 'bg-blue-100' : ''
                      } ${suggestion.type === 'history' ? 'border-b border-gray-100' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {suggestion.type === 'history' ? (
                            <History className="w-4 h-4 text-purple-500 mr-3" />
                          ) : (
                            <Search className="w-4 h-4 text-gray-400 mr-3" />
                          )}
                          <span className="text-gray-900">{suggestion.name}</span>
                        </div>
                        {suggestion.type === 'history' && (
                          <span className="text-xs text-purple-500 font-medium">
                            {t('recent')}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Create Downloader Code Section */}
        <div className="space-y-4 sm:space-y-6 py-6 sm:py-8 px-4">
          <p className="text-sm sm:text-base text-gray-800 font-medium">
            {t('createDownloader')}
          </p>
          <a
            href={DOWNLOADER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block transition-transform hover:scale-105"
          >
            <div className="downloader-button">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.18L17.82 7 12 9.82 6.18 7 12 4.18zM6 8.82l5 2.5V18.82l-5-2.5V8.82zm12 0v7.5l-5 2.5v-7.5l5-2.5z"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium">Downloader</span>
            </div>
          </a>
        </div>

        {/* Request New Code Section */}
        <div className="space-y-4 sm:space-y-6 py-6 sm:py-8 px-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
          <div className="text-center">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              {t('cantFindCode')}
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              {t('requestCodeDescription')}
            </p>
            <RequestCodeForm />
          </div>
        </div>

        {/* FAQ Section */}
        <div className="space-y-4 sm:space-y-6 py-6 sm:py-8 px-4">
          <p className="text-sm sm:text-base text-gray-800 font-medium">{t('faq')}</p>
          <button
            onClick={handleFAQ}
            className="inline-block transition-transform hover:scale-105"
            aria-label={t('faq')}
          >
            <div className="faq-icon">
              <HelpCircle className="w-12 h-12 sm:w-16 sm:h-16 text-blue-500" />
            </div>
          </button>
        </div>

        {/* Top Searches This Month */}
        {topSearches.length > 0 && (
          <div className="py-6 sm:py-8 mt-6 sm:mt-8 border-t border-gray-200 px-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6 text-center">
              🔥 {t('topSearches')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {topSearches.map((item, index) => (
                <div
                  key={item.code}
                  className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl sm:text-3xl font-bold text-blue-600">
                      #{index + 1}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-500 bg-blue-100 px-2 sm:px-3 py-1 rounded-full">
                      {item.search_count} {item.search_count === 1 ? 'search' : 'searches'}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                    {item.name}
                  </h3>
                  <code className="text-xl sm:text-2xl font-bold text-blue-600 block mb-3 sm:mb-4">
                    {item.code}
                  </code>
                  
                  {/* Download Button */}
                  <a
                    href={`${DOWNLOADER_URL}/${encodeURIComponent(item.code)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 sm:px-4 py-2 rounded-lg transition-colors w-full justify-center text-sm sm:text-base"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.18L17.82 7 12 9.82 6.18 7 12 4.18zM6 8.82l5 2.5V18.82l-5-2.5V8.82zm12 0v7.5l-5 2.5v-7.5l5-2.5z"/>
                    </svg>
                    Code Downloader
                  </a>
                  
                  {/* View Details Link */}
                  <button
                    onClick={() => navigate(`/searchresults?q=${encodeURIComponent(item.name)}`)}
                    className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm font-medium mt-2 w-full text-center underline"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms Link */}
        <div className="pt-8 sm:pt-12 px-4 pb-8">
          <a
            href="/terms"
            className="text-blue-500 hover:text-blue-700 underline transition-colors text-sm sm:text-base"
          >
            Terms and conditions.
          </a>
        </div>
      </main>

      {/* SEO Footer Section */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 sm:py-12 px-4 mt-12 sm:mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* About Section */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                About FireDL
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                FireDL is your ultimate source for <strong>working Downloader codes for Firestick and Android TV</strong>. 
                Discover the <strong>best Downloader codes for streaming apps</strong> and access secret app stores with ease.
              </p>
            </div>

            {/* Popular Searches */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                Popular Searches
              </h3>
              <ul className="text-xs sm:text-sm text-gray-600 space-y-2">
                <li>• Best Downloader Codes for Firestick</li>
                <li>• Working Downloader Codes 2025</li>
                <li>• Firestick App Codes</li>
                <li>• Android TV Downloader Codes</li>
                <li>• Secret App Stores for Firestick</li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                Categories
              </h3>
              <ul className="text-xs sm:text-sm text-gray-600 space-y-2">
                <li>• Streaming Apps</li>
                <li>• Media Players</li>
                <li>• Music & Podcasts</li>
                <li>• Sports & Live TV</li>
                <li>• Utilities & Tools</li>
              </ul>
            </div>
          </div>

          {/* SEO Keywords Section */}
          <div className="border-t border-gray-200 pt-6 sm:pt-8">
            <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
              Find Working Codes For:
            </h4>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Secret App Stores for Firestick & Android TV
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Best Downloader Codes for Firestick
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Working Downloader Codes for Firestick
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Firestick & Android TV Downloader Codes
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Best Downloader Codes for Streaming Apps
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full">
                Working Codes for Firestick Downloader
              </span>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <p className="text-xs sm:text-sm text-gray-500">
              {footerText}
            </p>
          </div>
        </div>
      </footer>

      {/* AdSense Code */}
      {adsenseSettings.enabled && adsenseSettings.code && (
        <div dangerouslySetInnerHTML={{ __html: adsenseSettings.code }} />
      )}

      {/* Cookie Consent */}
      {showCookie && <CookieConsent onAccept={() => setShowCookie(false)} />}

      {/* Scroll to Top */}
      <ScrollToTop />
    </div>
    </>
  );
};

export default HomePage;
